import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  CaretRightIcon,
  KeyIcon,
  PlusIcon,
  UsersThreeIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SearchBar } from '@/components/ui/search-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { GroupFormDrawer } from '@/components/admin/GroupFormDrawer';
import { ManageGroupsContent } from '@/content/ManageGroupsContent';
import { useCreateGroup, useGroups } from '@/lib/hooks/useGroups';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import type { ManageGroup, GroupStatusFilter } from '@/lib/types/groups.types';
import type { UserRole } from '@/lib/types/database';
import {
  filterGroups,
  formatGroupSectionSummary,
  getGroupRoleTone,
  groupGroupsByParentRole,
} from '@/lib/utils/groups.utils';
import { cn } from '@/lib/utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';

const STATUS_FILTERS: { value: GroupStatusFilter; label: string }[] = [
  { value: 'active', label: ManageGroupsContent.filters.status.active },
  { value: 'archived', label: ManageGroupsContent.filters.status.archived },
  { value: 'all', label: ManageGroupsContent.filters.status.all },
];

export function ManageGroups() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<GroupStatusFilter>('active');
  const [isFormOpen, setIsFormOpen] = useState(false);

  const includeArchived = statusFilter !== 'active';
  const { data: groups = [], isLoading, error } = useGroups(includeArchived);
  const { orderedKeys: orderedRoleKeys } = useRoleDirectory();
  const createGroupMutation = useCreateGroup();
  const { toast, showToast, hideToast } = useToastNotification();

  const filteredGroups = useMemo(
    () =>
      filterGroups(groups, {
        searchQuery,
        status: statusFilter,
      }),
    [groups, searchQuery, statusFilter]
  );

  const groupSections = useMemo(
    () => groupGroupsByParentRole(filteredGroups, orderedRoleKeys),
    [filteredGroups, orderedRoleKeys]
  );

  const openCreateDrawer = () => {
    setIsFormOpen(true);
  };

  const navigateToDetail = (group: ManageGroup) => {
    navigate({
      to: '/admin/manage-groups/$groupId',
      params: { groupId: group.id },
    });
  };

  const handleFormSubmit = async (input: {
    displayName: string;
    parentRole: UserRole;
    description: string | null;
  }) => {
    try {
      const savedGroup = await createGroupMutation.mutateAsync(input);

      setIsFormOpen(false);
      showToast({
        title: ManageGroupsContent.toast.createSuccessTitle,
        description: ManageGroupsContent.toast.createSuccessDescription(
          savedGroup.display_name
        ),
        variant: 'success',
      });
    } catch (submitError) {
      showToast({
        title: ManageGroupsContent.toast.errorTitle,
        description:
          submitError instanceof Error
            ? submitError.message
            : 'Failed to save group.',
        variant: 'error',
      });
    }
  };

  const isSubmitting = createGroupMutation.isPending;
  const showArchivedBanner = statusFilter !== 'active';

  return (
    <div className="bg-slate-700">
      <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="font-field-74 text-h1 leading-none text-slate-50">
              {ManageGroupsContent.pageTitle}
            </h1>
            <p className="mt-2 max-w-xl text-body text-slate-400">
              {ManageGroupsContent.pageDescription}
            </p>
          </div>
          <Button
            size="sm"
            className="mt-1 shrink-0 bg-purple-300 text-slate-700 hover:bg-purple-400"
            onClick={openCreateDrawer}
            aria-label={ManageGroupsContent.actions.create}
          >
            <PlusIcon size={20} weight="bold" />
            <span className="hidden sm:inline">
              {ManageGroupsContent.actions.create}
            </span>
            <span className="sm:hidden">Create</span>
          </Button>
        </div>

        <div className="mb-3">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={ManageGroupsContent.search.placeholder}
            className="[&_input]:border-slate-500 [&_input]:bg-slate-600 [&_input]:text-slate-50 [&_input]:placeholder:text-slate-500 [&_input]:focus-visible:border-purple-300"
          />
        </div>

        {/* Status filter tabs */}
        <div className="mb-4 flex gap-1 rounded-lg border border-slate-600 bg-slate-600/30 p-1">
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatusFilter(value)}
              className={cn(
                'flex-1 rounded-md px-3 py-1.5 text-small transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300',
                statusFilter === value
                  ? 'bg-slate-500 text-slate-50'
                  : 'text-slate-400 hover:text-slate-300'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {showArchivedBanner && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2.5 text-small text-amber-300">
            <WarningIcon size={14} weight="bold" className="shrink-0" />
            {ManageGroupsContent.filters.archivedBanner}
          </div>
        )}

        {!isLoading && (
          <div className="mb-3 flex items-center justify-between gap-4 text-small text-slate-500">
            <span>
              {ManageGroupsContent.counts.showing(
                filteredGroups.length,
                groups.length
              )}
            </span>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-3 text-body text-red-100">
            {error instanceof Error ? error.message : 'Failed to load groups.'}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-5">
            {[...Array(3)].map((_, sectionIndex) => (
              <div key={sectionIndex} className="space-y-2">
                <Skeleton className="h-6 w-40 rounded-full bg-slate-600" />
                <Skeleton className="h-20 w-full rounded-lg bg-slate-600" />
                <Skeleton className="h-20 w-full rounded-lg bg-slate-600" />
              </div>
            ))}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="rounded-lg border border-slate-500 bg-slate-600 px-4 py-10 text-center">
            <UsersThreeIcon
              size={32}
              weight="duotone"
              className="mx-auto mb-3 text-purple-300"
            />
            <p className="text-large text-slate-50">
              {groups.length === 0
                ? ManageGroupsContent.emptyStates.noGroups
                : ManageGroupsContent.emptyStates.noFilteredGroups}
            </p>
            {groups.length === 0 && (
              <p className="mx-auto mt-2 max-w-md text-body text-slate-300">
                {ManageGroupsContent.emptyStates.noGroupsDescription}
              </p>
            )}
            {groups.length === 0 && (
              <Button className="mt-5" onClick={openCreateDrawer}>
                <PlusIcon size={20} weight="bold" />
                {ManageGroupsContent.actions.create}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {groupSections.map((section) => (
              <GroupRoleSection
                key={section.parentRole}
                parentRole={section.parentRole}
                groups={section.groups}
                groupCount={section.groupCount}
                memberCount={section.memberCount}
                onNavigateToGroup={navigateToDetail}
              />
            ))}
          </div>
        )}
      </div>

      <GroupFormDrawer
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        group={null}
        isSubmitting={isSubmitting}
        onSubmit={handleFormSubmit}
      />

      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </div>
  );
}

interface GroupRoleSectionProps {
  parentRole: UserRole;
  groups: ManageGroup[];
  groupCount: number;
  memberCount: number;
  onNavigateToGroup: (group: ManageGroup) => void;
}

function GroupRoleSection({
  parentRole,
  groups,
  groupCount,
  memberCount,
  onNavigateToGroup,
}: GroupRoleSectionProps) {
  return (
    <section aria-label={`${getRoleLabelByKey(parentRole)} groups`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <RolePill parentRole={parentRole} />
        <span className="shrink-0 text-small text-slate-500">
          {formatGroupSectionSummary(groupCount, memberCount)}
        </span>
      </div>
      <div className="overflow-hidden rounded-lg bg-slate-600">
        {groups.map((group, index) => (
          <GroupListRow
            key={group.id}
            group={group}
            hasDivider={index > 0}
            onNavigateToGroup={onNavigateToGroup}
          />
        ))}
      </div>
    </section>
  );
}

interface GroupListRowProps {
  group: ManageGroup;
  hasDivider: boolean;
  onNavigateToGroup: (group: ManageGroup) => void;
}

function GroupListRow({
  group,
  hasDivider,
  onNavigateToGroup,
}: GroupListRowProps) {
  const isArchived = !!group.archived_at;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onNavigateToGroup(group);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="group-row"
      aria-label={`${group.display_name}`}
      className={cn(
        'flex cursor-pointer items-center gap-3 px-3 py-4 text-left transition-colors hover:bg-slate-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-inset sm:px-4',
        hasDivider && 'border-t border-slate-550 border-opacity-5',
        isArchived && 'opacity-60'
      )}
      onClick={() => onNavigateToGroup(group)}
      onKeyDown={handleKeyDown}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 truncate">
          <span
            className={cn(
              'truncate text-large',
              isArchived ? 'text-slate-400' : 'text-slate-50'
            )}
          >
            {group.display_name}
          </span>
          {isArchived && (
            <span className="shrink-0 rounded-full border border-amber-400/40 bg-amber-400/15 px-1.5 py-0.5 font-field-42 text-[9px] uppercase text-amber-300">
              Archived
            </span>
          )}
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-2 text-small text-slate-500">
          <KeyIcon size={13} weight="bold" className="shrink-0" />
          <code className="truncate font-mono text-[12px] text-slate-400">
            {group.import_key}
          </code>
        </div>
      </div>
      <div className="w-12 shrink-0 text-right">
        <div
          className={cn(
            'font-field-64 text-[20px] leading-5',
            isArchived ? 'text-slate-400' : 'text-slate-50'
          )}
        >
          {group.member_count}
        </div>
        <div className="mt-0.5 text-[10px] text-slate-500">
          {ManageGroupsContent.counts.memberLabel(group.member_count)}
        </div>
      </div>
      <CaretRightIcon
        size={16}
        weight="bold"
        className="shrink-0 text-slate-500"
      />
    </div>
  );
}

interface RolePillProps {
  parentRole: UserRole;
}

function RolePill({ parentRole }: RolePillProps) {
  const tone = getGroupRoleTone(parentRole);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[10px] uppercase text-slate-300',
        tone.pillClassName
      )}
    >
      <span className="h-1 w-1 rounded-full bg-current" />
      {getRoleLabelByKey(parentRole)}
    </span>
  );
}
